import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { calculateCartSubtotal } from '../utils/priceUtils';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, clearCart, getCartCount } = useCart();
  const { t, getProductName, getCategoryName, getDescription, getWeight, getCutType, getPackagingOption, formatLocalPrice, language, isRTL } = useLanguage();
  
  // Helper to convert numbers to Arabic numerals
  const toArabicNum = (num) => {
    if (!isRTL) return num;
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return String(num).replace(/[0-9]/g, (d) => arabicNumerals[parseInt(d)]);
  };
  
  const subtotal = calculateCartSubtotal(cartItems);
  const deliveryFee = subtotal > 500 ? 0 : 50; // Free delivery over 500 EGP
  const total = subtotal + deliveryFee;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50 py-20" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ShoppingBag className="mx-auto h-24 w-24 text-gray-400 mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('emptyCart')}</h2>
            <p className="text-lg text-gray-600 mb-8">
              {language === 'ar' ? 'يبدو أنك لم تضف أي منتجات بعد.' : "Looks like you haven't added any products yet."}
            </p>
            <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white">
              <Link to="/products">
                {isRTL ? <ArrowRight className="ml-2" size={20} /> : <ArrowLeft className="mr-2" size={20} />}
                {t('startShopping')}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <section className="bg-stone-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-2">{t('shoppingCart')}</h1>
          <p className="text-gray-300">
            {toArabicNum(getCartCount())} {language === 'ar' ? 'منتج في سلتك' : getCartCount() === 1 ? 'item' : 'items'} {language === 'en' ? 'in your cart' : ''}
          </p>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {language === 'ar' ? 'عناصر السلة' : 'Cart Items'}
                </h2>
                <Button 
                  variant="outline" 
                  onClick={clearCart}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Trash2 size={18} className={isRTL ? 'ml-2' : 'mr-2'} />
                  {language === 'ar' ? 'تفريغ السلة' : 'Clear Cart'}
                </Button>
              </div>

              {cartItems.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img
                        src={item.image}
                        alt={getProductName(item.name)}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{getProductName(item.name)}</h3>
                            <p className="text-sm text-gray-600">{getCategoryName(item.category)}</p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-600 hover:text-red-700 p-2"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{getDescription(item.description)}</p>
                        
                        {/* Selected Options */}
                        {item.selectedWeight && (
                          <div className="mb-3 text-sm text-gray-600">
                            <span className="font-medium">{language === 'ar' ? 'الخيارات:' : 'Options:'}</span> {getWeight(item.selectedWeight)} • {getCutType(item.selectedCut)} • {getPackagingOption(item.selectedPackaging)}
                          </div>
                        )}
                        
                        {/* Price */}
                        <div className="mb-3">
                          <span className="text-xl font-bold text-red-600">
                            {formatLocalPrice(item.price || 0)} {t('egp')}
                          </span>
                          <span className="text-sm text-gray-600 mx-2">
                            {language === 'ar' ? 'للوحدة' : 'per unit'}
                          </span>
                        </div>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center border border-gray-300 rounded-lg">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-2 hover:bg-gray-100 transition-colors"
                            >
                              <Minus size={18} />
                            </button>
                            <span className="px-4 py-2 font-semibold min-w-[3rem] text-center">
                              {toArabicNum(item.quantity)}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-2 hover:bg-gray-100 transition-colors"
                            >
                              <Plus size={18} />
                            </button>
                          </div>
                          <div className={isRTL ? 'mr-auto' : 'ml-auto'}>
                            <div className={isRTL ? 'text-left' : 'text-right'}>
                              <div className="text-sm text-gray-600">{t('subtotal')}</div>
                              <div className="text-xl font-bold text-gray-900">
                                {formatLocalPrice((item.price || 0) * item.quantity)} {t('egp')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('orderSummary')}</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-700">
                      <span>{t('subtotal')}:</span>
                      <span className="font-semibold">{formatLocalPrice(subtotal)} {t('egp')}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>{t('shipping')}:</span>
                      <span className="font-semibold">
                        {deliveryFee === 0 ? (
                          <span className="text-green-600">{t('freeShipping')}</span>
                        ) : (
                          `${formatLocalPrice(deliveryFee)} ${t('egp')}`
                        )}
                      </span>
                    </div>
                    {subtotal < 500 && subtotal > 0 && (
                      <div className="text-xs text-gray-600 bg-stone-50 p-3 rounded-lg">
                        {language === 'ar' 
                          ? `أضف ${formatLocalPrice(500 - subtotal)} جنيه للحصول على توصيل مجاني!`
                          : `Add ${formatLocalPrice(500 - subtotal)} EGP more for free delivery!`}
                      </div>
                    )}
                    <div className="border-t border-stone-200 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-gray-900">{t('total')}:</span>
                        <span className="text-2xl font-bold text-red-600">{formatLocalPrice(total)} {t('egp')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button asChild className="w-full bg-red-600 hover:bg-red-700 text-white" size="lg" data-testid="proceed-to-checkout">
                      <Link to="/checkout">
                        {t('proceedToCheckout')}
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full" size="lg">
                      <Link to="/products">
                        {isRTL ? <ArrowRight className="ml-2" size={18} /> : <ArrowLeft className="mr-2" size={18} />}
                        {t('continueShopping')}
                      </Link>
                    </Button>
                  </div>

                  <div className="mt-6 p-4 bg-stone-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {language === 'ar' ? 'تحتاج مساعدة؟' : 'Need Help?'}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {language === 'ar' 
                        ? 'تواصل معنا للطلبات الكبيرة والطلبات الخاصة'
                        : 'Contact us for bulk orders and special requests'}
                    </p>
                    <a href="tel:01270706065" className="text-sm text-red-600 hover:text-red-700">
                      📞 01270706065
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;